import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { uploadFile, deleteFile, storageBuckets } from '../../lib/supabase'
import { dateStr, uid } from '../../utils/formatters'
import { exportReportToPDF } from '../../utils/exportPDF'
import { SkeletonList } from '../ui/Skeleton'

const WEATHER_OPTIONS = [
  { value: 'cerah', label: '☀️ Cerah' },
  { value: 'berawan', label: '⛅ Berawan' },
  { value: 'hujan', label: '🌧️ Hujan' },
  { value: 'hujan deras', label: '⛈️ Hujan Deras' }
]

export default function Reports() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [reports, setReports] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [edit, setEdit] = useState(null)
  const [filterProject, setFilterProject] = useState('all')
  const [viewReport, setViewReport] = useState(null)

  const blank = {
    project_id: '',
    date: new Date().toISOString().split('T')[0],
    supervisor: '',
    workers_count: 0,
    activities: '',
    issues: '',
    weather: 'cerah'
  }
  const [form, setForm] = useState(blank)
  const [photos, setPhotos] = useState([])
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (!user) return
    fetchData()
  }, [user])

  const fetchData = async () => {
    setLoading(true)
    const [{ data: rep }, { data: proj }, { data: photosData }] = await Promise.all([
      supabase.from('daily_reports').select('*, projects(name)').eq('user_id', user.id).order('date', { ascending: false }),
      supabase.from('projects').select('*').eq('user_id', user.id),
      supabase.from('report_photos').select('*'),
    ])

    if (rep) setReports(rep)
    if (proj) setProjects(proj)
    if (photosData) setPhotos(photosData)
    setLoading(false)
  }

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    setUploading(true)
    const newPhotos = []

    for (const file of files) {
      try {
        const path = `${user.id}/reports/${uid()}-${file.name}`
        const result = await uploadFile(storageBuckets.photos, file, path)
        newPhotos.push({ id: uid(), file_path: result.path, file_url: result.url })
      } catch (error) {
        toast.show('Gagal upload: ' + file.name)
      }
    }

    setPhotos(prev => [...prev, ...newPhotos])
    setUploading(false)
  }

  const removePhoto = async (photoId, filePath) => {
    try {
      if (filePath) {
        await deleteFile(storageBuckets.photos, filePath)
      }
      setPhotos(prev => prev.filter(p => p.id !== photoId))
    } catch (error) {
      toast.show('Gagal menghapus foto')
    }
  }

  const save = async () => {
    if (!form.activities) {
      toast.show('Deskripsi pekerjaan wajib diisi')
      return
    }

    const data = {
      ...form,
      workers_count: parseInt(form.workers_count),
      user_id: user.id
    }

    let reportId = edit
    if (!edit) {
      reportId = uid()
      data.id = reportId
      await supabase.from('daily_reports').insert([data])
    } else {
      await supabase.from('daily_reports').update(data).eq('id', edit)
    }

    // Save photos
    const existingPhotos = photos.filter(p => p.report_id)
    const newPhotos = photos.filter(p => !p.report_id)

    for (const photo of newPhotos) {
      await supabase.from('report_photos').insert([{
        id: photo.id,
        report_id: reportId,
        file_path: photo.file_path,
        file_url: photo.file_url
      }])
    }

    setShow(false)
    setPhotos([])
    toast.show('Laporan disimpan')
    fetchData()
  }

  const del = async (id) => {
    const ok = await confirm('Hapus laporan ini beserta semua foto terkait?', {
      title: 'Hapus Laporan', icon: '📋', confirmLabel: 'Hapus', confirmVariant: 'danger'
    })
    if (!ok) return
    const reportPhotos = photos.filter(p => p.report_id === id)
    for (const photo of reportPhotos) {
      try { await deleteFile(storageBuckets.photos, photo.file_path) } catch (e) { }
    }
    await supabase.from('report_photos').delete().eq('report_id', id)
    await supabase.from('daily_reports').delete().eq('id', id)
    toast.show('Laporan dihapus')
    fetchData()
  }

  const filteredReports = filterProject === 'all'
    ? reports
    : reports.filter(r => r.project_id === filterProject)

  const getReportPhotos = (reportId) => {
    return photos.filter(p => p.report_id === reportId)
  }

  const handleExportPDF = (report) => {
    const project = projects.find(p => p.id === report.project_id)
    const reportPhotos = getReportPhotos(report.id)
    exportReportToPDF(report, project, reportPhotos)
    toast.show('PDF diunduh')
  }

  return (
    loading ? <SkeletonList count={4} /> :
      <div>
        <div className="filter-row">
          <select
            className="form-select"
            style={{ width: 200 }}
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
          >
            <option value="all">Semua Proyek</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <Button onClick={() => {
            setForm(blank)
            setEdit(null)
            setPhotos([])
            setShow(true)
          }}>
            📋 Tambah Laporan
          </Button>
        </div>

        {filteredReports.map(r => {
          const project = projects.find(p => p.id === r.project_id)
          const reportPhotos = getReportPhotos(r.id)
          const weatherIcon = WEATHER_OPTIONS.find(w => w.value === r.weather)?.label.split(' ')[0] || '☀️'

          return (
            <div key={r.id} className="card">
              <div className="card-header">
                <h3>
                  <span style={{ marginRight: 6 }}>{weatherIcon}</span>
                  {dateStr(r.date)}
                </h3>
                <div style={{ display: 'flex', gap: 4 }}>
                  <span className="badge badge-accent">{project?.name || 'Proyek'}</span>
                  <Button size="xs" variant="secondary" onClick={() => handleExportPDF(r)}>
                    📄 PDF
                  </Button>
                  <Button size="xs" variant="danger" onClick={() => del(r.id)}>
                    🗑️
                  </Button>
                </div>
              </div>
              <div className="card-body">
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                  gap: 6,
                  marginBottom: 8
                }}>
                  <div>
                    <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>SUPERVISOR</span>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{r.supervisor || '-'}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>PEKERJA</span>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                      {r.workers_count || 0} orang
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>PEKERJAAN</span>
                  <div style={{ fontSize: 12, marginTop: 2, whiteSpace: 'pre-line' }}>
                    {r.activities || '-'}
                  </div>
                </div>

                {r.issues && (
                  <div style={{
                    padding: 6,
                    background: 'var(--danger-bg)',
                    borderRadius: 'var(--radius)',
                    marginBottom: 6
                  }}>
                    <span style={{ fontSize: 9, color: 'var(--danger)' }}>⚠ KENDALA</span>
                    <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2 }}>
                      {r.issues}
                    </div>
                  </div>
                )}

                {reportPhotos.length > 0 && (
                  <div className="photo-grid">
                    {reportPhotos.map((ph) => (
                      <div key={ph.id} className="photo-thumb">
                        <img src={ph.file_url} alt="Foto laporan" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {!filteredReports.length && (
          <div className="empty-state">
            <p>Belum ada laporan harian</p>
            <Button
              variant="primary"
              style={{ marginTop: 12 }}
              onClick={() => { setForm(blank); setEdit(null); setPhotos([]); setShow(true) }}
            >
              📋 Buat Laporan Pertama
            </Button>
          </div>
        )}

        <Modal
          show={show}
          onClose={() => { setShow(false); setPhotos([]) }}
          title={edit ? '✏️ Edit Laporan' : '📋 Laporan Harian'}
          footer={
            <>
              <Button variant="secondary" onClick={() => { setShow(false); setPhotos([]) }}>Batal</Button>
              <Button onClick={save}>💾 Simpan</Button>
            </>
          }
        >
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Proyek</label>
              <select
                className="form-select"
                value={form.project_id}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              >
                <option value="">Pilih Proyek</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tanggal</label>
              <input
                className="form-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Supervisor</label>
              <input
                className="form-input"
                value={form.supervisor}
                onChange={(e) => setForm({ ...form, supervisor: e.target.value })}
                placeholder="Nama supervisor"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Jumlah Pekerja</label>
              <input
                className="form-input"
                type="number"
                value={form.workers_count}
                onChange={(e) => setForm({ ...form, workers_count: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Cuaca</label>
            <select
              className="form-select"
              value={form.weather}
              onChange={(e) => setForm({ ...form, weather: e.target.value })}
            >
              {WEATHER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Pekerjaan Hari Ini *</label>
            <textarea
              className="form-textarea"
              value={form.activities}
              onChange={(e) => setForm({ ...form, activities: e.target.value })}
              placeholder="Deskripsikan pekerjaan yang dilakukan..."
              style={{ minHeight: 100 }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Kendala / Masalah</label>
            <textarea
              className="form-textarea"
              value={form.issues}
              onChange={(e) => setForm({ ...form, issues: e.target.value })}
              placeholder="Jika ada kendala, tuliskan disini..."
              style={{ minHeight: 60 }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Foto Dokumentasi</label>
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              id="report-photos"
            />
            <label htmlFor="report-photos" className="photo-upload-zone">
              {uploading ? '🔄 Mengupload...' : '📸 Upload Foto'}
            </label>

            {photos.length > 0 && (
              <div className="photo-grid" style={{ marginTop: 6 }}>
                {photos.map((ph) => (
                  <div key={ph.id} className="photo-thumb">
                    <img src={ph.file_url} alt="Foto" />
                    <button
                      className="del-btn"
                      onClick={() => removePhoto(ph.id, ph.file_path)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      </div>
  )
}